import { PublicClientApplication } from '@azure/msal-browser'
import { useMSUserStore } from '@/stores/msUser'
import rtConfig from '@/configs/runtime'

// Declare msalInstance outside the composable for singleton behavior
let msalInstance = null

// Used for testing purposes to reset the MSAL instance
export const resetMsalInstance = () => {
  msalInstance = null
}

export const useMSAuth = () => {
  const userStore = useMSUserStore()

  const authConfig = rtConfig.get('authConfig')
  // Define the scope required to access the Inventory API
  const scopes = [authConfig.scope]

  const initializeMsal = async () => {
    if (!msalInstance) {
      const msalConfig = {
        auth: {
          clientId: authConfig.clientId,
          authority: `https://login.microsoftonline.com/${authConfig.tenantId}`,
          redirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: 'localStorage',
        },
      }
      msalInstance = new PublicClientApplication(msalConfig)
      await msalInstance.initialize()
    }
    return msalInstance
  }

  /**
   * Handles the redirect after login. This function checks if the user
   * is authenticated and sets the user data in the store. If no user
   * is found, it clears the user data in the store.
   */
  const handleRedirect = async () => {
    await initializeMsal()
    try {
      const authResult = await msalInstance.handleRedirectPromise()
      if (authResult && authResult.account) {
        userStore.setAccount(authResult.account)
      } else {
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          userStore.setAccount(accounts[0])
          if (accounts.length > 1) {
            // If multiple accounts are found, log a warning
            // and select the first account by default
            console.warn('Use the first by default on multiple accounts.')
          }
        } else {
          userStore.clear()
        }
      }
    } catch (error) {
      console.error('MSAL Redirect Error:', error)
    }
  }

  /**
   * Initiates the login process using MSAL. This function redirects
   * the user to the Microsoft login page. Upon successful login, the
   * user data is set in the store.
   */
  const login = async () => {
    await initializeMsal()
    try {
      const authResult = await msalInstance.loginPopup({ scopes })
      userStore.setAccount(authResult.account)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  /**
   * Logs the user out of the application. This function clears the
   * user data in the store and redirects the user to the logout page.
   * The user is redirected to the specified post-logout redirect URI.
   */
  const logout = async () => {
    if (userStore.isLoggedIn) {
      await initializeMsal()
      try {
        await msalInstance.logoutRedirect({ account: userStore.account })
        // Cleanup user data after successful logout
        userStore.clear()
      } catch (error) {
        console.error('Logout failed:', error)
        throw error
      }
    }
  }

  /**
   * Retrieves an access token for the authenticated user silently. This
   * token can be used for making authenticated API requests.
   *
   * @returns {Promise<string>} A promise that resolves to the user's
   * access token.
   * @throws {Error} If the user is not logged in or if the token cannot
   * be acquired silently.
   */
  const getAccessToken = async () => {
    if (!userStore.isLoggedIn) {
      throw new Error('User not authenticated. Please log in to continue.')
    }

    await initializeMsal()
    const authResult = await msalInstance.acquireTokenSilent({
      scopes,
      account: userStore.account,
    })
    return authResult.accessToken
  }

  return {
    handleRedirect,
    login,
    logout,
    getAccessToken,
    getUser: () => userStore,
  }
}
